import { Message } from '../../types/message.types';
import { CodeTab } from '../../types/project.types';

function formatTimestamp(dateString: string): string {
  const d = new Date(dateString);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${dd}.${mm}.${yyyy}-${hh}:${min}`;
}

export interface MessageBubbleProps {
  message: Message;
  onOpenFile?: (file: CodeTab) => void;
}

interface AssistantPayload {
  frontend?: string;
  backend?: string;
  database?: string;
  explanation?: string;
}

const FILE_TABS: { key: CodeTab; label: string }[] = [
  { key: 'frontend', label: 'Frontend' },
  { key: 'backend', label: 'Backend' },
  { key: 'database', label: 'Database' },
];

function AssistantBubble({
  content,
  onOpenFile,
}: {
  content: string;
  onOpenFile?: (file: CodeTab) => void;
}) {
  let parsed: AssistantPayload | null = null;
  try {
    let raw = content.trim();
    if (raw.startsWith('```')) {
      raw = raw.replace(/^```(?:json)?\n?/, '').replace(/\n?```$/, '').trim();
    }
    parsed = JSON.parse(raw);
  } catch {
    // Not structured JSON — show as plain text.
  }

  if (!parsed?.explanation) {
    return <p className="text-sm whitespace-pre-wrap">{content}</p>;
  }

  const generatedFiles = FILE_TABS.filter(({ key }) => !!parsed?.[key]);

  return (
    <div>
      <p className="text-sm whitespace-pre-wrap mb-3">{parsed.explanation}</p>
      {generatedFiles.length > 0 && (
        <div className="border-t border-gray-100 pt-2.5">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 mb-2">
            Generated files
          </p>
          <div className="flex flex-wrap gap-1.5">
            {generatedFiles.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => onOpenFile?.(key)}
                className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border border-gray-200 bg-gray-50 text-gray-600 hover:border-orange-300 hover:bg-orange-50 hover:text-orange-700 transition-colors"
              >
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 10 10"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M2 5h6M5 2l3 3-3 3" />
                </svg>
                {label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function MessageBubble({ message, onOpenFile }: MessageBubbleProps) {
  const isUser = message.role === 'user';

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}>
      <div
        className={`max-w-[85%] rounded-lg px-4 py-3 ${
          isUser
            ? 'bg-orange-100 text-white'
            : 'bg-white text-gray-900 border border-gray-200 shadow-sm'
        }`}
      >
        {isUser ? (
          <p className="text-sm whitespace-pre-wrap">{message.content}</p>
        ) : (
          <AssistantBubble content={message.content} onOpenFile={onOpenFile} />
        )}
        <span
          className={`text-xs ${isUser ? 'text-blue-800' : 'text-gray-800'} mt-2 block`}
        >
          {formatTimestamp(message.createdAt)}
        </span>
      </div>
    </div>
  );
}
